import { usePreviewFile } from '@features/components/ClinicalData/hooks';
import { convertMediaFiles } from '@features/components/ClinicalData/utils';
import { Carousel } from '@ui';
import { useMemo } from 'react';
import { useStateList } from 'react-use';
import { IconButton } from '@ds';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { VitalsFormatted } from '@features/components/ClinicalData/sections/utils';
import { MediaPreview } from '@features/components/ClinicalData/components/MediaPreview/MediaPreview';
import { LogItemLayout } from '@features/components/ClinicalData/components/ClinicalLog/components/LogItemLayout';
import { BaseEvent } from '@features/components/ClinicalData/components/ClinicalLog/components/BaseEvent';
import { VitalsLogGrid } from '@features/components/ClinicalData/components/ClinicalLog/components/VitalsLogGrid';
import { ClinicalDataToShare } from '@features/components/ClinicalData/types';

type VitalsItemProps = {
  vitals: VitalsFormatted[];
  event: string;
  description?: string;
  onShareClick?: (activeRecord?: ClinicalDataToShare) => void;
  shared?: boolean;
};

export const VitalsItem = ({ vitals, description, event, shared, onShareClick }: VitalsItemProps) => {
  const { prev, next, currentIndex, isFirst, isLast } = useStateList(vitals);
  const mediaList = useMemo(
    () => vitals.flatMap((item) => (item.file ? convertMediaFiles([item.file], item.timestamp, item.timeZone) : [])),
    [vitals],
  );
  const { isPreviewOpen, previewFileId, handlePreviewOpen, handlePreviewClose } = usePreviewFile();

  return (
    <LogItemLayout className="gap-2 bg-base-70">
      <div className="w-full flex flex-col gap-2">
        <BaseEvent
          shared={shared}
          event={event}
          onShareClick={onShareClick ? () => onShareClick(vitals[currentIndex]) : undefined}
        />
        <div className="flex justify-start gap-2 w-full">
          <Carousel currentIndex={currentIndex} itemClassName="flex-1">
            {vitals.map((v) => (
              <VitalsLogGrid key={v.timestamp} item={v} handlePreviewOpen={handlePreviewOpen} />
            ))}
          </Carousel>
        </div>
        {description && <div className="pt-2 text-s-r text-base-10">{description}</div>}
      </div>
      {isPreviewOpen && (
        <MediaPreview
          heading="Vitals"
          media={mediaList}
          onClose={handlePreviewClose}
          open={isPreviewOpen}
          selectedMediaId={previewFileId}
          onShareClick={onShareClick}
          isFromClinicalLog
        />
      )}
      <div className="flex justify-between items-center">
        <div className="text-base-20 text-s-r">
          {vitals.length} Reading{vitals.length === 1 ? '' : 's'}
        </div>
        <div className="flex gap-2">
          <IconButton
            variant="filled"
            size="xs"
            disableRipple
            isDisabled={isFirst}
            startContent={<MdKeyboardArrowLeft />}
            onPress={prev}
          />
          <IconButton
            variant="filled"
            size="xs"
            disableRipple
            isDisabled={isLast}
            startContent={<MdKeyboardArrowRight />}
            onPress={next}
          />
        </div>
      </div>
    </LogItemLayout>
  );
};
